import { OrderApprovalStatus } from 'src/apollo/onlineOrdering'
import { dateToDayString, toLocalTime } from 'src/shared/js/timeUtils'
import { t } from 'src/translations/src'
import { UnifiedOrderStatus } from '../order_tracker/order_status_bar/statusUtil'

export type GetOrderStatusSectionTextsParams = {
  unifiedOrderStatus: UnifiedOrderStatus
  isDeliveryOrder: boolean
  restaurantName?: string
  customerEmail?: string | null
  /** `orderStatusInfo.lastUpdated` ISO string when available */
  lastUpdatedIso?: string | null
  approvalStatus?: OrderApprovalStatus | null
}

export const getOrderStatusSectionTexts = ({
  unifiedOrderStatus,
  isDeliveryOrder,
  restaurantName,
  customerEmail,
  lastUpdatedIso,
  approvalStatus
}: GetOrderStatusSectionTextsParams): {
  headerText: string
  subtitle: string
} => {
  const lastUpdatedTime = lastUpdatedIso ? toLocalTime(lastUpdatedIso) : null
  const lastUpdatedDate = lastUpdatedIso ? new Date(lastUpdatedIso) : null

  const preparingText = {
    headerText: t('order-confirmation.order-status.preparing'),
    subtitle: t('order-confirmation.order-status.preparing-subtitle', {
      restaurantName
    })
  }

  const confirmedText = {
    headerText: t('order-confirmation.order-status.confirmed'),
    subtitle: t('order-confirmation.order-status.confirmed-subtitle', {
      restaurantName
    })
  }

  switch (unifiedOrderStatus) {
    case UnifiedOrderStatus.SCHEDULED:
      return {
        headerText: t('order-confirmation.order-status.scheduled'),
        subtitle: t('order-confirmation.order-status.scheduled-subtitle', {
          behaviorType: isDeliveryOrder
            ? t('order-confirmation.order-status.delivery-lowercase')
            : t('order-confirmation.order-status.pickup-lowercase')
        })
      }
    case UnifiedOrderStatus.RECEIVED:
      // Upstream reports RECEIVED for an order the restaurant has not acted on
      // yet, so RECEIVED alone does not mean the restaurant confirmed anything.
      if (approvalStatus === OrderApprovalStatus.NeedsApproval) {
        return {
          headerText: t('order-confirmation.order-status.placed'),
          subtitle: t('order-confirmation.order-status.placed-subtitle', {
            restaurantName
          })
        }
      }
      return confirmedText
    case UnifiedOrderStatus.CONFIRMED:
      return confirmedText
    case UnifiedOrderStatus.PREPARING:
      return preparingText
    case UnifiedOrderStatus.LIKELY_READY:
      return isDeliveryOrder
        ? preparingText
        : {
            headerText: t('order-confirmation.order-status.likely-ready'),
            subtitle: t('order-confirmation.order-status.likely-ready-subtitle')
          }
    case UnifiedOrderStatus.READY_FOR_PICKUP:
      return isDeliveryOrder
        ? preparingText
        : {
            headerText: t('order-confirmation.order-status.ready'),
            subtitle: t('order-confirmation.order-status.ready-subtitle')
          }
    case UnifiedOrderStatus.DRIVER_EN_ROUTE_TO_RESTAURANT:
    case UnifiedOrderStatus.DRIVER_AT_RESTAURANT:
      return {
        headerText: t('order-confirmation.order-status.picking-up'),
        subtitle: t('order-confirmation.order-status.picking-up-subtitle', {
          restaurantName
        })
      }
    case UnifiedOrderStatus.DRIVER_EN_ROUTE_TO_DROPOFF:
      return {
        headerText: t('order-confirmation.order-status.out-for-delivery'),
        subtitle: t('order-confirmation.order-status.out-for-delivery-subtitle')
      }
    case UnifiedOrderStatus.DRIVER_AT_DROPOFF:
      return {
        headerText: t('order-confirmation.order-status.driver-nearby'),
        subtitle: t('order-confirmation.order-status.driver-nearby-subtitle')
      }
    case UnifiedOrderStatus.COMPLETE:
      if (lastUpdatedTime && lastUpdatedDate) {
        return {
          headerText: t('order-confirmation.order-status.complete'),
          subtitle: isDeliveryOrder
            ? t('order-confirmation.order-status.complete-subtitle-delivered', {
                date: dateToDayString(lastUpdatedDate),
                time: lastUpdatedTime
              })
            : t('order-confirmation.order-status.complete-subtitle-picked-up', {
                date: dateToDayString(lastUpdatedDate),
                time: lastUpdatedTime
              })
        }
      }
      return {
        headerText: t('order-confirmation.order-status.complete'),
        subtitle: isDeliveryOrder
          ? t(
              'order-confirmation.order-status.complete-subtitle-delivered-no-time'
            )
          : t(
              'order-confirmation.order-status.complete-subtitle-picked-up-no-time'
            )
      }
    default:
      return {
        headerText: t('order-confirmation.thank-you'),
        subtitle: t('order-confirmation.confirmation-sent', {
          email: customerEmail
        })
      }
  }
}
